/**
 * Secciones del reglamento del wizard (paso 2): el borrador se guarda como un
 * solo `content` markdown con encabezados `## <Sección>`; estas funciones
 * convierten entre ese texto y los 4 campos del formulario.
 *
 * Nota de la regresión que motivó extraer esto a lib: el regex original se
 * construía con template literal donde `\s`/`\S` NO son escapes válidos y se
 * volvían literales `s`/`S` — el parseo nunca capturaba nada, el fallback
 * volcaba TODO el documento dentro de "Aclaraciones" y cada tecleo se perdía
 * en el ciclo parsear⇄componer.
 */

export const SETUP_RULE_FIELDS = [
  {
    key: "inscripciones",
    label: "Inscripciones y pagos",
    hint: "Condiciones particulares de inscripción, pagos, reembolsos o comprobantes.",
    placeholder: "Ej. El pago debe enviarse antes del cierre de inscripciones. No hay reembolsos después del sorteo.",
  },
  {
    key: "conducta",
    label: "Conducta y puntualidad",
    hint: "Reglas propias del club, tolerancias, W/O, código de conducta y sanciones.",
    placeholder: "Ej. Tolerancia de 15 minutos. Jugador que no se presente pierde por W/O.",
  },
  {
    key: "logistica",
    label: "Logística y sede",
    hint: "Indicaciones de cancha, horarios, pelotas, uniformes, estacionamiento o check-in.",
    placeholder: "Ej. Presentarse 20 minutos antes en mesa de control. Uso obligatorio de calzado adecuado.",
  },
  {
    key: "aclaraciones",
    label: "Aclaraciones adicionales",
    hint: "Premios, contacto del organizador, casos no previstos o mensajes importantes.",
    placeholder: "Ej. Cualquier situación no prevista será resuelta por el comité organizador.",
  },
] as const;

export type SetupRuleFieldKey = typeof SETUP_RULE_FIELDS[number]["key"];

export type SetupRuleSections = Record<SetupRuleFieldKey, string>;

export function emptySetupRuleSections(): SetupRuleSections {
  return SETUP_RULE_FIELDS.reduce((acc, field) => {
    acc[field.key] = "";
    return acc;
  }, {} as SetupRuleSections);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function parseSetupRuleContent(content: string): SetupRuleSections {
  const sections = emptySetupRuleSections();
  let matched = false;
  for (const field of SETUP_RULE_FIELDS) {
    // Sin flag "m": el `$` debe anclar al FINAL del contenido, no de cada línea.
    const regex = new RegExp(`(?:^|\\n)## ${escapeRegExp(field.label)}\\n([\\s\\S]*?)(?=\\n## |$)`);
    const match = content.match(regex);
    if (match) {
      sections[field.key] = match[1].trim();
      matched = true;
    }
  }
  if (!matched && content.trim()) {
    // Borradores viejos de texto libre (sin encabezados) caen completos aquí.
    sections.aclaraciones = content.trim();
  }
  return sections;
}

export function composeSetupRuleContent(sections: SetupRuleSections): string {
  return SETUP_RULE_FIELDS
    .map((field) => {
      const value = sections[field.key].trim();
      return value ? `## ${field.label}\n${value}` : "";
    })
    .filter(Boolean)
    .join("\n\n");
}
